import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, X, Image as ImageIcon } from "lucide-react";
import { renderDiaryImages, safePosterUrl } from "@/components/diaryImages";
import { groupByMonth, ratingLabel, showAccent, dateParts, computeHighlights } from "@/components/DiaryPoster";

/** Everything typed by a person goes through this before it is put in the page, so nothing they wrote can run as code. */
export function esc(v) {
  return String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

const lines = (v) => esc(v).replace(/\n/g, "<br>");
const stars = (n) => (n ? "★".repeat(n) + "☆".repeat(5 - n) : "");

const PALETTES = [["#7a1020", "#2a0a12"], ["#4a2a6a", "#160d22"], ["#1f4a5a", "#0a1a20"], ["#6a4a12", "#1f1608"]];
const hash = (s) => { let h = 0; for (const c of s || "") h = (h * 31 + c.charCodeAt(0)) >>> 0; return h; };
function initials(name) {
  const words = (name || "").replace(/[^\p{L}\p{N}\s]/gu, " ").split(/\s+/).filter(Boolean);
  const skip = new Set(["the", "a", "an", "of", "and"]);
  const main = words.filter((w) => !skip.has(w.toLowerCase()));
  return (main.length ? main : words).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
}

const CSS = `
@page { size: A4; margin: 10mm 0; }
* { box-sizing: border-box; }
html, body, * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
html { background: #0D0A0E; }
body { margin: 0; background: #0D0A0E; color: #E8E2D8; font-family: Arial, Helvetica, sans-serif; line-height: 1.5; }
.serif { font-family: Georgia, "Times New Roman", serif; }
.bar { position: sticky; top: 0; z-index: 5; padding: 10px 18px; background: #000; border-bottom: 1px solid #4A3A1F; font-size: 13px; color: #A89FA9; display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
.bar button { background: #C89B3C; color: #0D0A0E; border: 0; border-radius: 999px; padding: 9px 20px; font-weight: 700; font-size: 14px; cursor: pointer; }
.page { max-width: 760px; margin: 0 auto; padding: 28px 18px 36px; }
.brand { text-align: center; font-family: Georgia, serif; font-size: 15px; letter-spacing: 6px; color: #C89B3C; text-transform: uppercase; margin-bottom: 18px; }
.hero { position: relative; overflow: hidden; border: 1px solid rgba(200,155,60,.4); border-radius: 24px; padding: 34px 24px 28px; text-align: center; background: linear-gradient(135deg, #7a1020, #2a1620 55%, #0D0A0E); }
.hero:before { content: ""; position: absolute; left: 0; right: 0; top: 0; height: 5px; background: linear-gradient(90deg, transparent, #C89B3C, transparent); }
.kicker { font-size: 12px; font-weight: 700; letter-spacing: 4px; color: #E6C071; text-transform: uppercase; }
.hero h1 { margin: 12px 0 0; font-family: Georgia, serif; font-size: 34px; line-height: 1.2; color: #FAF7F2; overflow-wrap: anywhere; }
.stars3 { margin-top: 14px; font-size: 15px; letter-spacing: 10px; color: #C89B3C; }
.summary { margin-top: 12px; font-size: 14px; color: rgba(255,248,230,.8); }
.scope { margin-top: 4px; font-size: 12px; color: #A89FA9; }
.highlights { display: flex; gap: 10px; margin-top: 14px; }
.hl { flex: 1; min-width: 0; border: 1px solid rgba(200,155,60,.25); border-radius: 16px; padding: 12px 14px; background: linear-gradient(135deg, rgba(200,155,60,.1), #161118); break-inside: avoid; }
.hl small { display: block; font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: #A89FA9; }
.hl b { display: block; margin-top: 4px; font-family: Georgia, serif; font-size: 15px; line-height: 1.3; color: #E6C071; overflow-wrap: anywhere; }
.month { display: flex; align-items: center; gap: 12px; margin: 26px 0 12px; break-after: avoid; }
.ribbon { padding: 4px 18px 4px 16px; border-radius: 0 999px 999px 0; background: linear-gradient(90deg, #7a1020, #9E1B28); font-family: Georgia, serif; font-size: 14px; font-weight: 700; color: #FAF7F2; }
.line { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(200,155,60,.45), transparent); }
.count { font-size: 12px; color: #A89FA9; }
.card { display: flex; margin: 0 0 12px; overflow: hidden; border: 1px solid #4A3A1F; border-left-width: 4px; border-radius: 16px; background: linear-gradient(135deg, #161118, #1d1319); break-inside: avoid; }
.poster { position: relative; flex: 0 0 84px; min-height: 126px; overflow: hidden; background: #1a1218; }
.poster .ph { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-family: Georgia, serif; font-size: 24px; font-weight: 700; letter-spacing: 1px; color: #E6C071; }
.poster .ph:before { content: ""; position: absolute; inset: 6px; border: 1px solid rgba(200,155,60,.5); border-radius: 6px; }
.poster img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.body { flex: 1; min-width: 0; padding: 12px 14px; }
.body h3 { margin: 0; font-family: Georgia, serif; font-size: 18px; line-height: 1.25; color: #FAF7F2; overflow-wrap: anywhere; }
.body p { margin: 5px 0 0; font-size: 13px; color: #E8E2D8; overflow-wrap: anywhere; }
.body .meta { font-size: 12px; color: #A89FA9; }
.rate { font-size: 15px; letter-spacing: 1px; color: #C89B3C; }
.rate span { margin-left: 6px; font-size: 12px; letter-spacing: 0; color: #E6C071; }
.verdict { font-family: Georgia, serif; font-style: italic; color: #FAF7F2 !important; font-size: 14px !important; }
.tags span { display: inline-block; margin: 6px 4px 0 0; padding: 2px 10px; border: 1px solid rgba(200,155,60,.45); border-radius: 999px; font-size: 11px; color: #E6C071; }
.private { margin-top: 8px; padding-left: 10px; border-left: 2px dashed rgba(200,155,60,.6); }
.moment { font-style: italic; color: #E6C071 !important; }
.stub { flex: 0 0 74px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; padding: 0 6px; text-align: center; border-left: 1px dashed rgba(200,155,60,.45); background: rgba(0,0,0,.25); }
.stub .m { font-size: 10px; font-weight: 700; letter-spacing: 2px; color: #C89B3C; }
.stub .d { font-family: Georgia, serif; font-size: 30px; font-weight: 700; line-height: 1; color: #FAF7F2; }
.stub .y { font-size: 11px; color: #A89FA9; }
.empty { text-align: center; color: #A89FA9; font-style: italic; padding: 30px 0; }
.foot { margin-top: 26px; text-align: center; font-size: 12px; line-height: 1.7; color: #8F8591; }
.foot .gold { color: #C89B3C; letter-spacing: 8px; }
@media (max-width: 520px) { .hero h1 { font-size: 26px; } .highlights { flex-direction: column; } .poster { flex-basis: 64px; } .stub { flex-basis: 58px; } .stub .d { font-size: 24px; } }
@media print { .bar { display: none; } .page { padding-top: 0; } }
`;

/** The printable page, as one self-contained HTML string, themed like the site and the emails. Pure, so it can be tested without a browser.
 *  entries: the person's diary entries, newest first. includePrivate: also print who they went with, seat, cast and favourite moment.
 *  base: where relative poster addresses are measured from (the site's address in the browser). */
export { safePosterUrl };

export function buildDiaryHtml({ entries, title, scope, includePrivate, generated, autoPrint = true, base }) {
  const rated = entries.filter((e) => e.rating);
  const avg = rated.length ? (rated.reduce((a, e) => a + e.rating, 0) / rated.length).toFixed(1) : "";
  const theatres = new Set(entries.map((e) => e.venue).filter(Boolean)).size;
  const bits = [`🎟️ ${entries.length} ${entries.length === 1 ? "show" : "shows"}`, avg && `⭐ ${avg} average`, theatres && `🏛️ ${theatres} ${theatres === 1 ? "theatre" : "theatres"}`].filter(Boolean);
  const h = computeHighlights(entries, new Date().getFullYear());
  const cards = [
    h.top && ["⭐ Top rated", h.top.name],
    h.fav ? ["🏛️ Favourite theatre", `${h.fav.name} · ${h.fav.visits} visits`] : theatres ? ["🏛️ Theatres visited", String(theatres)] : null,
    ["📅 Shows this year", String(h.thisYear)],
  ].filter(Boolean);

  const entryHtml = (e) => {
    const [a, b] = PALETTES[hash(e.show_name) % PALETTES.length];
    const src = safePosterUrl(e.image_url, base);
    const dp = dateParts(e.date);
    const priv = [];
    if (includePrivate) {
      const where = [e.seat_area && `Seat: ${esc(e.seat_area)}`, e.went_with && `With: ${esc(e.went_with)}`].filter(Boolean).join("  ·  ");
      if (where) priv.push(`<p class="meta">${where}</p>`);
      if (e.cast_seen?.length) priv.push(`<p class="meta">Cast seen: ${e.cast_seen.map((c) => esc(c.role ? `${c.actor_name} (${c.role})` : c.actor_name)).join(", ")}</p>`);
      if (e.favourite_moment) priv.push(`<p class="moment">✨ ${esc(e.favourite_moment)}</p>`);
    }
    return `<article class="card" style="border-left-color:${showAccent(e.show_name || "")}">
  <div class="poster" style="background:linear-gradient(160deg,${a},${b})"><div class="ph">${esc(initials(e.show_name))}</div>${src ? `<img src="${esc(src)}" alt="">` : ""}</div>
  <div class="body">
    <h3>${esc(e.show_name)}</h3>
    ${e.venue ? `<p class="meta">${esc(e.venue)}</p>` : ""}
    ${e.rating ? `<p class="rate">${stars(e.rating)}<span>${esc(ratingLabel(e.rating))}</span></p>` : ""}
    ${e.verdict ? `<p class="verdict">“${esc(e.verdict)}”</p>` : ""}
    ${e.tags?.length ? `<div class="tags">${e.tags.map((t) => `<span>${esc(t)}</span>`).join("")}</div>` : ""}
    ${e.note ? `<p>${lines(e.note)}</p>` : ""}
    ${e.review ? `<p>${lines(e.review)}</p>` : ""}
    ${e.best_performance ? `<p class="meta">Best performance: ${esc(e.best_performance)}</p>` : ""}
    ${e.recommend_for ? `<p class="meta">Great for: ${esc(e.recommend_for)}</p>` : ""}
    ${e.would_see_again === true ? `<p class="meta">👍 Would see again</p>` : e.would_see_again === false ? `<p class="meta">Wouldn't see again</p>` : ""}
    ${priv.length ? `<div class="private">${priv.join("")}</div>` : ""}
  </div>
  <div class="stub"><span class="m">${esc(dp.month)}</span><span class="d">${esc(dp.day)}</span><span class="y">${esc(dp.year)}</span></div>
</article>`;
  };

  const body = entries.length === 0
    ? `<p class="empty">No diary entries to show.</p>`
    : groupByMonth(entries).map((g) => `<section><div class="month"><span class="ribbon">${esc(g.label)}</span><span class="line"></span><span class="count">${g.items.length} ${g.items.length === 1 ? "show" : "shows"}</span></div>\n${g.items.map(entryHtml).join("\n")}</section>`).join("\n");

  const script = `document.querySelectorAll('.poster img').forEach(function(i){i.addEventListener('error',function(){i.remove()})});`
    + (autoPrint ? `window.addEventListener('load',function(){setTimeout(function(){window.print()},600)});` : "");

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><meta name="robots" content="noindex"><meta name="color-scheme" content="dark">
<title>${esc(title)}</title>
<style>${CSS}</style></head><body>
<div class="bar"><button type="button" onclick="window.print()">Save as PDF / Print</button><span>Choose “Save as PDF” as the destination. On a phone, use Share, then Save to Files.</span></div>
<div class="page">
<div class="brand">✦ My Theatre ✦</div>
<header class="hero"><div class="kicker">🎭 Theatre diary</div><h1>${esc(title)}</h1><div class="stars3">✦ ✦ ✦</div><div class="summary">${bits.map(esc).join("&nbsp;&nbsp;·&nbsp;&nbsp;")}</div><div class="scope">${esc(scope)}</div></header>
${entries.length > 1 ? `<div class="highlights">${cards.map(([k, v]) => `<div class="hl"><small>${esc(k)}</small><b>${esc(v)}</b></div>`).join("")}</div>` : ""}
${body}
<div class="foot"><div class="gold">✦ ✦ ✦</div>Exported from My Theatre (mytheatre.uk) on ${esc(generated)}${includePrivate ? "<br>Includes private details. Keep this file to yourself if you don't want them shared." : ""}</div>
</div>
<script>${script}</script>
</body></html>`;
}

/** The "Download PDF" button and its small options box. */
export default function DiaryExport({ entries, username, defaultYear = "all" }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(defaultYear);
  const [priv, setPriv] = useState(true);
  const years = useMemo(() => [...new Set(entries.map((e) => (e.date || "").slice(0, 4)).filter(Boolean))].sort().reverse(), [entries]);

  const [making, setMaking] = useState(false);
  const [pics, setPics] = useState(null);          // { urls, files, posters } once the pictures are made
  useEffect(() => () => { (pics?.urls || []).forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* ignore */ } }); }, [pics]);
  const canShareFiles = (files) => { try { return !!(navigator.canShare && navigator.share && navigator.canShare({ files })); } catch { return false; } };

  const meta = () => {
    const picked = year === "all" ? entries : entries.filter((e) => (e.date || "").startsWith(year));
    return { picked, title: username ? `${username}'s Theatre Diary` : "My Theatre Diary", scope: year === "all" ? "All shows" : `Shows in ${year}`,
      generated: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) };
  };
  const makePictures = async () => {
    const { picked, title, scope, generated } = meta();
    setMaking(true);
    try {
      const r = await renderDiaryImages({ entries: picked, title, scope, includePrivate: priv, generated });
      const slug = (username || "my-theatre-diary").toLowerCase().replace(/[^a-z0-9-]+/g, "-");
      const files = r.blobs.map((b, i) => new File([b], `${slug}-${year}-${String(i + 1).padStart(2, "0")}.png`, { type: "image/png" }));
      setPics({ urls: r.blobs.map((b) => URL.createObjectURL(b)), files, posters: r.posters, wanted: new Set(picked.map((e) => e.image_url).filter(Boolean)).size });
      setOpen(false);
    } catch { toast.error("Could not make the pictures. Please try again, or use the PDF option."); }
    finally { setMaking(false); }
  };
  const saveToPhotos = async () => {
    try { await navigator.share({ files: pics.files, title: "My Theatre diary" }); }
    catch (e) { if (e && e.name !== "AbortError") toast.error("Could not open the share menu. Use Download pictures, or press and hold a picture to save it."); }
  };
  const downloadAll = () => pics.files.forEach((f, i) => setTimeout(() => {
    const a = document.createElement("a"); a.href = pics.urls[i]; a.download = f.name; document.body.appendChild(a); a.click(); a.remove();
  }, i * 400));
  const closePics = () => setPics(null);

  const go = () => {
    const picked = year === "all" ? entries : entries.filter((e) => (e.date || "").startsWith(year));
    const html = buildDiaryHtml({
      entries: picked, title: username ? `${username}'s Theatre Diary` : "My Theatre Diary",
      scope: year === "all" ? "All shows" : `Shows in ${year}`, includePrivate: priv,
      generated: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
    });
    const w = window.open("", "_blank");
    if (!w) { toast.error("Your browser blocked the new window. Allow pop-ups for this site, then try again."); return; }
    w.document.open(); w.document.write(html); w.document.close();
    setOpen(false);
  };

  if (entries.length === 0) return null;
  return (
    <>
      <button type="button" onClick={() => { setYear(defaultYear); setOpen(true); }} data-testid="export-open"
        className="inline-flex items-center gap-1.5 rounded-full border border-[#C89B3C]/60 px-4 py-2 text-sm font-medium hover:bg-[#C89B3C]/15">
        <Download className="h-4 w-4" /> Download diary
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Download your diary">
          <div className="w-full max-w-md rounded-t-2xl border border-border bg-card p-5 sm:rounded-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl font-semibold">Download your diary</h2>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="rounded-full p-1.5 hover:bg-secondary"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 space-y-4 text-sm">
              <div>
                <label htmlFor="export-year" className="mb-1 block font-medium">Which shows?</label>
                <select id="export-year" data-testid="export-year" value={year} onChange={(e) => setYear(e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2">
                  <option value="all">Everything ({entries.length})</option>
                  {years.map((y) => <option key={y} value={y}>{y} ({entries.filter((e) => (e.date || "").startsWith(y)).length})</option>)}
                </select>
              </div>
              <label className="flex items-start gap-2">
                <input type="checkbox" className="mt-1" checked={priv} onChange={(e) => setPriv(e.target.checked)} data-testid="export-private" />
                <span><span className="font-medium">Include private details</span><span className="block text-xs text-muted-foreground">Who you went with, your seat, the cast you saw and your favourite moment. Untick this if you plan to share the file.</span></span>
              </label>
              <button type="button" onClick={makePictures} disabled={making} data-testid="export-pictures"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#C89B3C] px-5 py-3 text-sm font-semibold text-black disabled:opacity-60">
                <ImageIcon className="h-4 w-4" /> {making ? "Making your pictures…" : "Save as pictures (for your photos)"}
              </button>
              <p className="text-xs text-muted-foreground">Pictures are best on a phone: they go to your photo gallery, ready to post or send. Or use the PDF below.</p>
              <p className="text-xs text-muted-foreground">PDF: this opens a clean page of your diary in a new tab. Choose “Save as PDF” when your browser's print box appears. On a phone, use Share, then Save to Files. Nothing is stored on our servers.</p>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-full px-5 py-2 text-sm hover:bg-secondary">Cancel</button>
              <button type="button" onClick={go} data-testid="export-go" className="rounded-full border border-[#C89B3C]/60 px-6 py-2 text-sm font-semibold hover:bg-[#C89B3C]/15">Open PDF view</button>
            </div>
          </div>
        </div>
      )}
      {pics && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Your diary pictures">
          <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-2xl border border-border bg-card p-5 sm:rounded-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl font-semibold">Your diary pictures ({pics.files.length})</h2>
              <button type="button" onClick={closePics} aria-label="Close" className="rounded-full p-1.5 hover:bg-secondary"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-3 flex flex-col gap-2">
              {canShareFiles(pics.files) && (
                <button type="button" onClick={saveToPhotos} data-testid="pics-share" className="rounded-full bg-[#C89B3C] px-5 py-3 text-sm font-semibold text-black">Save to Photos / Gallery</button>
              )}
              <button type="button" onClick={downloadAll} data-testid="pics-download" className="rounded-full border border-[#C89B3C]/60 px-5 py-2.5 text-sm font-medium hover:bg-[#C89B3C]/15">Download pictures</button>
              <p className="text-xs text-muted-foreground">
                {canShareFiles(pics.files) ? "Choose “Save Image” (iPhone) or your Gallery (Android) in the menu. " : ""}You can also press and hold any picture below and choose “Save to Photos” or “Download image”.
              </p>
              {pics.wanted > pics.posters && (
                <p className="text-xs text-muted-foreground" data-testid="pics-posters-note">{pics.posters} of {pics.wanted} posters could be included. The others show coloured initials instead.</p>
              )}
            </div>
            <div className="mt-3 grid min-h-0 flex-1 grid-cols-2 gap-3 overflow-y-auto" data-testid="pics-grid">
              {pics.urls.map((u, i) => <img key={u} src={u} alt={`Diary picture ${i + 1} of ${pics.urls.length}`} className="w-full rounded-lg border border-border" />)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}